import { IngredientsContext } from "@/app/clientLayout";
import { annotateFileLocal } from "@/lib/annotate-file-local";
import useIndexedDB from "@/lib/useIndexedDB";
import { classNames } from "@/lib/util";
import { AnnotationResponseData } from "@/pages/api/recipe-annotate";
import { ResponseData } from "@/pages/api/recipe-annotation-to-template";
import {
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import DropzoneContainer from "@/components/styles/DropzoneContainer";
import { AnimatePresence, motion } from "framer-motion";
import { DefaultApiResponse, RecipeTemplate } from "@/lib/types";
import { nanoid } from "nanoid";
import Link from "next/link";
import { useContext, useState } from "react";
import Dropzone from "react-dropzone";
import toast from "react-hot-toast";
import DisplayImageFile from "../DisplayImageFile";
import DisplayPDFFile from "../DisplayPDFFile";
import DisplayWordFile from "../DisplayWordFile";
import { CurrentUser } from "../Page";
import FinalisePartialRecipe, { PartialRecipe } from "./FinalisePartialRecipe";
import { uploadedRecipesToPartialRecipes } from "./util";

interface Props {
  setUploadingFn?: (uploading: boolean) => void;
  onCompleted?: () => void;
  user: CurrentUser;
}

const UploadRecipeFile: React.FC<Props> = ({
  setUploadingFn,
  onCompleted,
  user,
}) => {
  const { ingredients, setIngredients } = useContext(IngredientsContext);

  const [locked, setLocked] = useIndexedDB("uploadRecipeLocked", false);

  const [uploading, setUploading] = useIndexedDB(
    "uploadRecipeUploading",
    false,
    !user.saveRecipeProgress
  );
  const [uuid, setUuid] = useIndexedDB<string | null>(
    "uploadRecipeUuid",
    null,
    !user.saveRecipeProgress
  );
  const [recipeTemplates, setRecipeTemplates] = useIndexedDB<RecipeTemplate[]>(
    "uploadRecipeTemplate",
    [],
    !user.saveRecipeProgress
  );
  const [templating, setTemplating] = useState(false);
  const [recipeData, setRecipeData] = useIndexedDB<PartialRecipe[] | null>(
    "uploadRecipeData3",
    null,
    !user.saveRecipeProgress
  );

  const [numFiles, setNumFiles] = useState(0);
  const [numFilesUploaded, setNumFilesUploaded] = useState(0);
  const [numFilesAnnotated, setNumFilesAnnotated] = useState(0);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showImage, setShowImage] = useState(true);

  const [PDFFile, setPDFFile] = useState<File | null>(null);
  const [showPDF, setShowPDF] = useState(true);

  const [WordFile, setWordFile] = useState<File | null>(null);
  const [showWord, setShowWord] = useState(true);

  const reset = () => {
    // setLocked(true);

    setUploading(false);
    if (setUploadingFn) setUploadingFn(false);

    setNumFiles(0);
    setNumFilesUploaded(0);
    setNumFilesAnnotated(0);

    setUuid(null);
    setRecipeTemplates([]);
    setRecipeData(null);

    setImageFile(null);
    setPDFFile(null);
    setWordFile(null);
  };

  // const resetCounters = () => {
  //   setNumFilesUploaded(0);
  //   setNumFilesAnnotated(0);
  // }

  const handleFiles: (files: File[]) => void = async (files) => {
    const finalWorkSheets: PartialRecipe[] = [];
    let numFiles = 0;

    for (const file of files) {
      console.log("TYPE:", file.type);

      try {
        // do normal things
        const uuid = `${Date.now()}-${nanoid(7)}`;
        setUuid(uuid);

        let formData = new FormData();
        formData.append("media", file);
        formData.append("uuid", uuid);

        const uploadRes = await fetch("/api/recipe-upload", {
          method: "POST",
          body: formData,
        });

        const { data: uploadData, error: uploadError }: DefaultApiResponse =
          await uploadRes.json();

        setNumFilesUploaded((prev) => prev + 1);
        if (uploadError || !uploadData) {
          toast.error(
            uploadError ||
              `Something went wrong while uploading file ${file.name}`
          );
          return;
        }

        console.log("File was uploaded successfully:", uploadData);

        if (
          [
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          ].includes(file.type)
        ) {
          // set word file
          setWordFile(file);

          // do word/ excel things
          const annotation = await annotateFileLocal(file);

          const annotateRes = await fetch(
            `/api/recipe-annotation-to-template`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                annotation,
                uuid,
              }),
            }
          );

          const { template: annotateData, error: annotateError }: ResponseData =
            await annotateRes.json();

          setNumFilesAnnotated((prev) => prev + 1);
          if (annotateError || !annotateData) {
            toast.error(
              annotateError ||
                `Something went wrong while annotating file ${file.name}`
            );

            if (!recipeData) {
              setUploading(false);
              if (setUploadingFn) setUploadingFn(false);
            }

            return;
          }

          console.log("File was annotated successfully:", annotateData);

          const tempRecipeData = await uploadedRecipesToPartialRecipes(
            user,
            ingredients,
            annotateData
          );

          console.log("TEMP RECIPE DATA", tempRecipeData);

          if (recipeData) {
            // add recipes
            setRecipeData([...recipeData, ...tempRecipeData]);
          } else {
            // set recipes
            setRecipeData(tempRecipeData);
          }
        } else {
          // Check if a pdf
          if (file.type === "application/pdf") {
            setPDFFile(file);
          } else if (file.type.startsWith("image/")) {
            setImageFile(file);
          }

          // your time will come
          // do image/pdf things
          const annotateRes = await fetch(
            `/api/recipe-annotate?uuid=${encodeURIComponent(uuid)}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const {
            annotation: annotateData,
            error: annotateError,
          }: AnnotationResponseData = await annotateRes.json();
          setNumFilesAnnotated((prev) => prev + 1);
          if (annotateError || !annotateData) {
            toast.error(
              annotateError ||
                `Something went wrong while annotating file ${file.name}`
            );

            if (!recipeData) {
              setUploading(false);
              if (setUploadingFn) setUploadingFn(false);
            }

            return;
          }

          const templateRes = await fetch(
            `/api/recipe-annotation-to-template`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                annotation: annotateData,
                uuid,
              }),
            }
          );

          const { template: templateData, error: templateError }: ResponseData =
            await templateRes.json();

          if (templateError || !templateData) {
            toast.error(
              templateError ||
                `Something went wrong while templating file ${file.name}`
            );

            if (!recipeData) {
              setUploading(false);
              if (setUploadingFn) setUploadingFn(false);
            }

            return;
          }

          console.log("File was annotated successfully:", annotateData);

          const tempRecipeData = await uploadedRecipesToPartialRecipes(
            user,
            ingredients,
            templateData
          );

          console.log("TEMP RECIPE DATA", tempRecipeData);

          if (recipeData) {
            // add recipes
            setRecipeData([...recipeData, ...tempRecipeData]);
          } else {
            // set recipes
            setRecipeData(tempRecipeData);
          }
        }
      } catch (error) {
        console.error(error);
        toast.error(`Error uploading file "${file.name}"`);

        if (!recipeData) {
          setUploading(false);
          if (setUploadingFn) setUploadingFn(false);
        }
      }
    }
  };

  return (
    <AnimatePresence>
      {locked ? null : (
        <motion.div
          key={"beta-banner"}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, transition: { delay: 0.5 } }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.5,
            // delay: 0.5,
          }}
          // className="flex h-full w-full flex-col items-center justify-center"
        >
          <div className="mb-8 max-w-2xl rounded-lg bg-cyan-50 p-2.5 shadow">
            <div className="flex">
              <div className="flex-shrink-0">
                <InformationCircleIcon
                  className="w-5 text-blue-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-2 flex-1 md:flex md:justify-between">
                <p className="text-blue-700">
                  If you encounter any issues with this feature, please{" "}
                  <button
                    onClick={() => {
                      (window as any).Intercom(
                        "showNewMessage",
                        "I encountered an issue with the recipe upload feature."
                      );
                    }}
                    className="font-medium text-blue-700 underline"
                  >
                    let us know
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      {locked ? (
        <motion.div
          key={"locked"}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, transition: { delay: 0.5 } }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.5,
            // delay: 0.5,
          }}
          className="flex h-full w-full flex-col items-center justify-center"
        >
          <h1>Currently in Limited Beta Testing</h1>
          <p className="mb-16 mt-2 text-xl font-normal text-gray-500">
            Enter the code to unlock the feature.
          </p>

          <label htmlFor="unlock-upload">
            <input
              id="unlock-upload"
              type="text"
              className="w-full max-w-md rounded border border-gray-300 p-1.5 text-3xl"
              autoFocus
              onChange={(e) => {
                if (e.target.value === "expecto patronum") {
                  setLocked(false);
                }
              }}
            />
          </label>

          <p className="mt-16 text-xl font-normal text-gray-500">
            If you would like to participate in the beta test,{" "}
            <button
              className="inline font-medium text-cyan-900 hover:text-black"
              onClick={() => {
                (window as any).Intercom(
                  "showNewMessage",
                  "Hi, I would like to participate in the beta test for the upload feature."
                );
              }}
            >
              let us know
            </button>
            .
          </p>
        </motion.div>
      ) : !uploading ? (
        <motion.div
          key={"unlocked"}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, transition: { delay: 0.5 } }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.5,
            // delay: 0.5,
          }}
        >
          {/* <button
            onClick={() => {
              setLocked(true);
            }}
            className="mb-12"
          >
            <LockOpen className="w-8 text-gray-400 transition-colors hover:text-emerald-400" />
          </button> */}

          <Dropzone
            accept={[
              "application/vnd.ms-excel", // .xls
              ".xls",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
              ".xlsx",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
              ".docx",
              "application/pdf", // .pdf
              ".pdf",
              "image/*", // .jpg, .png, .gif
              ".jpg",
              ".jpeg",
              ".png",
              ".gif",
            ]}
            onDrop={(acceptedFiles, rejectedFiles) => {
              if (!acceptedFiles.length) {
                toast.error(
                  "You can only upload image, pdf, Word or Excel files"
                );
                return;
              }

              if (rejectedFiles.length) {
                toast(
                  "Some files were rejected. Please make sure all files are supported."
                );
              }

              setUploading(true);
              if (setUploadingFn) {
                setUploadingFn(true);
              }

              console.log("Added files", acceptedFiles);
              setNumFiles(acceptedFiles.length);
              setNumFilesUploaded(0);
              setNumFilesAnnotated(0);

              handleFiles(acceptedFiles.filter((_, index) => index === 0));
            }}
          >
            {({
              getRootProps,
              getInputProps,
              isDragAccept,
              isDragReject,
              isDragActive,
            }) => (
              <section>
                <DropzoneContainer
                  {...getRootProps({
                    isDragAccept,
                    isDragReject,
                    isDragActive,
                  })}
                  style={{
                    width: "600px",
                    maxWidth: "100%",
                  }}
                >
                  <input {...getInputProps()} />
                  <h1 className="text-black">
                    Drop files here, or click to select files
                  </h1>
                  <p className="mt-2 text-xl font-medium text-gray-400">
                    Supports image, PDF, Word or Excel files
                  </p>
                </DropzoneContainer>
              </section>
            )}
          </Dropzone>
        </motion.div>
      ) : numFiles !== numFilesUploaded || numFiles !== numFilesAnnotated ? (
        <motion.div
          key={"uploading"}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, transition: { delay: 0.5 } }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.5,
            // delay: 0.5,
          }}
          className="flex h-full w-full flex-col items-center justify-center"
        >
          <div className="spinner m-0 h-11 w-11" />
          <h2 className="mt-4 text-gray-800">
            Uploading {numFiles} file{numFiles > 1 ? "s" : ""}
          </h2>
          {numFilesAnnotated >= numFiles ? (
            <p className="mt-2 text-xl font-medium text-green-400">DONE!</p>
          ) : (
            <p className="mt-2 text-lg font-medium text-gray-400">
              {numFilesAnnotated === numFilesUploaded
                ? "Uploading"
                : "Annotating"}{" "}
              file {numFilesAnnotated + 1}
            </p>
          )}
          <div
            className={classNames(
              "mt-8 h-2.5 w-full max-w-md rounded-lg bg-gray-300 transition",
              numFilesUploaded + numFilesAnnotated === numFiles * 2
                ? "scale-110"
                : ""
            )}
          >
            <div
              className="h-full rounded-lg bg-green-500 transition-all ease-in-out"
              style={{
                width: `${
                  ((numFilesUploaded + numFilesAnnotated) / (numFiles * 2)) *
                  100
                }%`,
              }}
            />
          </div>

          <button className="mt-16">
            <XCircleIcon
              className="w-8 text-gray-400 transition-colors hover:text-emerald-400"
              onClick={() => {
                setRecipeData(null);
                setUploading(false);
                if (setUploadingFn) {
                  setUploadingFn(false);
                }
              }}
            />
          </button>
        </motion.div>
      ) : !recipeData ? (
        <motion.div
          key={"template"}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, transition: { delay: 0.5 } }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.5,
            // delay: 0.5,
          }}
          className="flex h-full w-full flex-col items-center justify-center"
        >
          <button
            className="btn mb-12"
            onClick={() => {
              reset();
            }}
          >
            Restart
          </button>

          <div className="spinner h-18 w-18" />

          {PDFFile ? <DisplayPDFFile file={PDFFile} /> : null}
          {imageFile ? <DisplayImageFile file={imageFile} /> : null}
          {WordFile ? <DisplayWordFile file={WordFile} /> : null}
        </motion.div>
      ) : (
        <motion.div
          key="finalise"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, transition: { delay: 0.5 } }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            duration: 0.5,
            // delay: 0.5,
          }}
          className="flex h-full w-full flex-col items-center justify-center"
        >
          <button
            className="btn mb-12"
            onClick={() => {
              reset();
            }}
          >
            Restart
          </button>

          {PDFFile ? (
            <div className="mb-8">
              <button
                className="btn btn-subtle"
                onClick={() => {
                  setShowPDF((prev) => !prev);
                }}
              >
                {showPDF ? "Hide" : "Show"} PDF
              </button>
            </div>
          ) : null}
          {imageFile ? (
            <div className="mb-8">
              <button
                className="btn btn-subtle"
                onClick={() => {
                  setShowImage((prev) => !prev);
                }}
              >
                {showPDF ? "Hide" : "Show"} Image
              </button>
            </div>
          ) : null}
          {WordFile ? (
            <div className="mb-8">
              <button
                className="btn btn-subtle"
                onClick={() => {
                  setShowWord((prev) => !prev);
                }}
              >
                {showWord ? "Hide" : "Show"} Word Document
              </button>
            </div>
          ) : null}

          <div className="relative flex justify-around gap-16">
            {PDFFile && showPDF ? <DisplayPDFFile file={PDFFile} /> : null}
            {imageFile && showImage ? (
              <DisplayImageFile file={imageFile} />
            ) : null}
            {WordFile && showWord ? <DisplayWordFile file={WordFile} /> : null}

            <div className="flex-1">
              <FinalisePartialRecipe
                initialRecipes={recipeData}
                updateRecipes={setRecipeData}
                onCompleted={(recipeIDs) => {
                  // toast, with a link to the recipe, if only one recipe was created
                  toast.success((t) => (
                    <div className="flex flex-row items-center gap-2">
                      {recipeData.some((r) => r.image) ? (
                        <img
                          src={
                            recipeData.find((r) => r.image)?.image ||
                            "/placeholder.png"
                          }
                          alt="Recipe Image"
                          className="max-h-26 max-w-16 rounded object-cover"
                        />
                      ) : null}
                      <div>
                        {recipeIDs.length > 1 ? (
                          <p>
                            {recipeIDs.length} recipes have been created. You
                            can view and edit them from the recipes page.
                          </p>
                        ) : (
                          <p>
                            <span className="font-normal">
                              {recipeData[0].name}
                            </span>{" "}
                            has been created. You can view and edit it from the
                            recipes page.
                          </p>
                        )}
                        {recipeIDs.length === 1 ? (
                          <Link
                            prefetch={false}
                            href={`/recipes?folder=${
                              recipeIDs[0].folderID || ""
                            }#${recipeIDs[0].id}`}
                            className="btn btn-sm btn-subtle"
                            onClick={() => {
                              toast.dismiss(t.id);
                            }}
                          >
                            View Recipe
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ));

                  reset();

                  if (onCompleted) {
                    onCompleted();
                  }
                }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UploadRecipeFile;
